"use client";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  ensureCsrf,
  createSession,
  sendChatMessage,
  sendChatMessageWithFile,
  updateSession,
  getSession,
  listMessages,
  listMessagesByConversation,
} from "@/lib/api";
import { mapModelSelection } from "@/lib/aiMap";
import {
  precheckCanSend,
  getUserId,
  estimatePromptTokens,
  applyUsageCost,
} from "@/lib/credits";
import {
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Tooltip,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";

export default function ChatBot() {
  const router = useRouter();
  // Ensure CSRF cookie exists for POSTs (some backends require it)
  useEffect(() => {
    try {
      ensureCsrf();
    } catch {}
  }, []);

  // If path is /c/:id, bind to that session/conversation id
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const m = /\/c\/(\d+)/.exec(window.location.pathname);
      if (m) {
        const sid = Number(m[1]);
        if (!Number.isNaN(sid) && sid > 0) {
          setSessionId(sid);
          setConversationId(sid);
          try {
            localStorage.setItem("active_session_id", String(sid));
            localStorage.setItem("active_conversation_id", String(sid));
          } catch {}
        }
      }
    } catch {}
  }, []);

  const TH = {
    fileLabel: "ไฟล์:",
    loading: "กำลังพิมพ์...",
    attach: "แนบไฟล์ (PDF / รูปภาพ)",
    placeholder: "พิมพ์ข้อความของคุณ...",
    apiError: "เกิดข้อผิดพลาดในการเรียก API",
  } as const;

  const [messages, setMessages] = useState<
    {
      sender: string;
      text: string;
      fileName?: string;
      promptTokens?: number;
      completionTokens?: number;
      totalTokens?: number;
    }[]
  >([]);
  const lastUsageRef = useRef<{
    prompt?: number;
    completion?: number;
    total?: number;
  } | null>(null);
  const userIndexRef = useRef<number | null>(null);
  const botIndexRef = useRef<number | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [modelChoice, setModelChoice] = useState<{
    provider?: string;
    model?: string;
  }>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [agentActive, setAgentActive] = useState(false);
  const [knowledgeCount, setKnowledgeCount] = useState(0);
  
  // Quick start helpers
  const quickSend = (text: string) => {
    if (loading) return;
    try {
      setInput(text);
      // Ensure send uses the new input value in next tick
      setTimeout(() => sendMessage(), 0);
    } catch {}
  };

  // Frequently-asked questions (dynamic a bit based on context)
  function getDefaultFaqs(): { label: string; query: string }[] {
    const base: { label: string; query: string }[] = [
      { label: 'สรุปเอกสารนี้ให้สั้น กระชับ', query: 'สรุปเอกสารที่แนบให้สั้น กระชับ เป็นหัวข้อย่อย' },
      { label: 'มีประเด็นสำคัญอะไรบ้าง', query: 'สกัดประเด็นสำคัญ พร้อมคำอธิบายสั้นๆ' },
      { label: 'ยกตัวอย่างการใช้งานจริง', query: 'ยกตัวอย่างสถานการณ์ที่เกี่ยวข้องและแนวทางปฏิบัติ' },
      { label: 'ทำเช็กลิสต์ให้หน่อย', query: 'ทำเช็กลิสต์ข้อควรทำ/ข้อห้ามจากข้อมูลนี้' },
      { label: 'แหล่งอ้างอิงอยู่ที่ไหน', query: 'อ้างอิงที่มา/หน้า/หัวข้อของข้อมูลที่ใช้ตอบ' },
    ];
    if (knowledgeCount > 0) {
      base.unshift({ label: 'ค้นหาคำตอบจากชุดความรู้', query: 'ตอบจากข้อมูลในชุดความรู้ที่แนบเท่านั้น ถ้าไม่พบให้บอกว่าไม่พบ' });
    }
    return base.slice(0, 8);
  }

  const [dbFaqs, setDbFaqs] = useState<{ label: string; query: string }[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/faq', { cache: 'no-store' });
        if (!res.ok) throw new Error('faq');
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        const clean = items
          .filter((x: any) => x && x.label && x.query)
          .map((x: any) => ({ label: String(x.label), query: String(x.query) }));
        if (!cancelled) setDbFaqs(clean);
      } catch {
        if (!cancelled) setDbFaqs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Reflect agent/knowledge meta for this session so UI can show badges
  function refreshSessionMeta(id: number | null) {
    if (!id) { setAgentActive(false); setKnowledgeCount(0); return; }
    try {
      const sys = (typeof window !== 'undefined') ? (localStorage.getItem(`agent_system:${id}`) || '') : '';
      const desc = (typeof window !== 'undefined') ? (localStorage.getItem(`agent_desc:${id}`) || '') : '';
      setAgentActive(!!(sys && sys.trim()) || !!(desc && desc.trim()));
      let kn = 0;
      try {
        const raw = localStorage.getItem(`agent_knowledge:${id}`);
        if (raw) {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) kn = arr.length || 0;
        }
      } catch {}
      try {
        const type = (typeof window !== 'undefined') ? (localStorage.getItem('active_explore_type') || '') : '';
        if (type !== 'knowledge') {
          kn = 0; // Agent or none: do not show Knowledge badge
        } else if (type === 'knowledge' && !kn) {
          kn = 1; // Knowledge active but items not counted; show badge
        }
      } catch {}
      setKnowledgeCount(kn);
    } catch { setAgentActive(false); setKnowledgeCount(0); }
  }

  useEffect(() => { refreshSessionMeta(sessionId); }, [sessionId]);
  useEffect(() => {
    const onStorage = () => refreshSessionMeta(sessionId);
    const onSwitched = () => refreshSessionMeta(sessionId);
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      window.addEventListener('sessionSwitched' as any, onSwitched as any);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
        window.removeEventListener('sessionSwitched' as any, onSwitched as any);
      }
    };
  }, [sessionId]);

  // ---- Render helpers: Markdown-lite + JSON + image preview ----
  // Match fully-qualified URLs and common root-relative asset paths
  const urlRegex =
    /(https?:\/\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+|\/(?:uploads|img|images|static|public)\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+)/gi;
  const imgExtRegex = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;

  function isImageLink(u: string): boolean {
    if (!u) return false;
    if (/^data:image\//i.test(u)) return true;
    if (imgExtRegex.test(u)) return true;
    if (/[?&](?:format|ext)=(?:png|jpg|jpeg|gif|webp|bmp|svg)/i.test(u))
      return true;
    return false;
  }

  // Auto model routing by message/file heuristics
  function pickAutoModel(message: string, attachedFile?: File | null): { provider?: string; model?: string } {
    const m = String(message || '').trim();
    const f = attachedFile || null;
    const isImageFile = !!(f && (f.type?.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(f.name || '')));
    const hasImgUrl = (m.match(urlRegex) || []).some(u => isImageLink(u));
    if (isImageFile || hasImgUrl) return { provider: 'auto', model: 'openai/gpt-5-image' };

    const web = /(latest|current\s*events|web\s*search|news|breaking|browse|search\s+the\s+web|internet\s+search)/i;
    if (web.test(m)) return { provider: 'openrouter', model: 'perplexity/sonar' };

    const code = /```|class\s+\w+|function\s+\w+|import\s+|#include|npm\s+|pip\s+install|stack\s*trace|TypeScript|Python|JavaScript|React|Node\b/i;
    if (code.test(m)) return { provider: 'openrouter', model: 'deepseek/deepseek-chat' };

    const longOrReason = m.length > 1200 || /(reason(?:ing)?|analy[sz]e|step\s*by\s*step|detailed|explain|long\s*answer)/i.test(m);
    if (longOrReason) return { provider: 'openrouter', model: 'anthropic/claude-3.5-sonnet' };

    return { provider: 'auto', model: 'openai/gpt-5' };
  }

  function tryParseJSON(text: string): any | null {
    try {
      const t = (text || "").trim();
      if (!(t.startsWith("{") || t.startsWith("["))) return null;
      return JSON.parse(t);
    } catch {
      return null;
    }
  }

  function CodeBlock({ code }: { code: string }) {
    return (
      <pre className="w-full overflow-auto rounded-md bg-gray-800/70 text-gray-100 text-sm p-3 border border-gray-700">
        <code>{code}</code>
      </pre>
    );
  }

  function MessageBody({ text }: { text: string }) {
    const json = tryParseJSON(text);
    if (json) {
      return <CodeBlock code={JSON.stringify(json, null, 2)} />;
    }

    // Parse ``` fenced code blocks (simple)
    const parts: Array<{ type: "code" | "text"; value: string }> = [];
    let remaining = text || "";
    while (true) {
      const start = remaining.indexOf("```");
      if (start === -1) {
        parts.push({ type: "text", value: remaining });
        break;
      }
      const before = remaining.slice(0, start);
      const after = remaining.slice(start + 3);
      const end = after.indexOf("```");
      if (end === -1) {
        parts.push({ type: "text", value: remaining });
        break;
      }
      const codeBody = after.slice(0, end).replace(/^\w+\n/, ""); // drop optional language line
      if (before) parts.push({ type: "text", value: before });
      parts.push({ type: "code", value: codeBody });
      remaining = after.slice(end + 3);
    }

    return (
      <div className="space-y-2">
        {parts.map((p, i) => {
          if (p.type === "code") return <CodeBlock key={i} code={p.value} />;

          // Text: auto-link URLs and preview images
          const segs: any[] = [];
          let last = 0;
          const matches = [...(p.value.matchAll(urlRegex) || [])];
          if (matches.length === 0) {
            segs.push(
              <span key={0} className="whitespace-pre-wrap break-words">
                {p.value}
              </span>
            );
          } else {
            matches.forEach((m, idx) => {
              const u = m[0];
              const s = m.index || 0;
              if (s > last)
                segs.push(
                  <span
                    key={`t-${idx}`}
                    className="whitespace-pre-wrap break-words"
                  >
                    {p.value.slice(last, s)}
                  </span>
                );
              segs.push(
                <a
                  key={`a-${idx}`}
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-300 underline break-all"
                >
                  {u}
                </a>
              );
              last = s + u.length;
            });
            if (last < p.value.length)
              segs.push(
                <span key={`t-end`} className="whitespace-pre-wrap break-words">
                  {p.value.slice(last)}
                </span>
              );
          }
          const images = matches.map((m) => m[0]).filter((u) => isImageLink(u));
          return (
            <div key={i}>
              <div className="whitespace-pre-wrap break-words">{segs}</div>
              {images.length > 0 && (
                <div className="mt-2 space-y-2">
                  {images.map((u, k) => (
                    <img
                      key={k}
                      src={u}
                      alt="image"
                      className="max-h-64 rounded border border-gray-700"
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Global drag & drop: accept image/file into composer
  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      setDragActive(true);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const f =
        (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) ||
        null;
      if (f) setFile(f as any);
    };
    const onDragLeave = () => setDragActive(false);
    window.addEventListener("dragover", onDragOver as any);
    window.addEventListener("drop", onDrop as any);
    window.addEventListener("dragleave", onDragLeave as any);
    return () => {
      window.removeEventListener("dragover", onDragOver as any);
      window.removeEventListener("drop", onDrop as any);
      window.removeEventListener("dragleave", onDragLeave as any);
    };
  }, []);

  // Build preview URL when a file is chosen
  useEffect(() => {
    if (!file) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      return;
    }
    try {
      const url = URL.createObjectURL(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch {}
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [file]);

  useEffect(() => {
    ensureCsrf().catch(() => {});
    if (typeof window !== "undefined") {
      const id = localStorage.getItem("selectedModel") || "auto";
      // Sanitize legacy selection: downgrade vision-only id back to text model
      const sanitized = /(^|\/)gpt-5-image$/i.test(id) ? "openai/gpt-5" : id;
      setModelChoice(mapModelSelection(sanitized));
      if (sanitized !== id) {
        try {
          localStorage.setItem("selectedModel", sanitized);
          window.dispatchEvent(
            new CustomEvent("modelChanged", { detail: sanitized })
          );
        } catch {}
      }
      const sid = localStorage.getItem("active_session_id");
      if (sid) setSessionId(Number(sid));
      const cid = localStorage.getItem("active_conversation_id");
      if (cid) setConversationId(Number(cid));
      setHydrated(true);
    }
  }, []);

  // React to model change from NavBar immediately
  useEffect(() => {
    const onModelChanged = async (e: any) => {
      const id = (e?.detail as string) || "auto";
      const nextChoice = mapModelSelection(id);
      console.log("Model changed:", id, "?", nextChoice);
      setModelChoice(nextChoice);
      if (sessionId) {
        try {
          console.log("Updating session:", sessionId);
          await updateSession(sessionId, nextChoice as any);
        } catch {
          setSessionId(null);
        }
      }
    };
    const onSessionSwitched = (e: any) => {
      const id = Number(e?.detail);
      if (!Number.isNaN(id)) {
        setSessionId(id);
        setMessages([]);
      }
    };
    window.addEventListener("modelChanged", onModelChanged);
    window.addEventListener("sessionSwitched", onSessionSwitched);
    return () => {
      window.removeEventListener("modelChanged", onModelChanged);
      window.removeEventListener("sessionSwitched", onSessionSwitched);
    };
  }, [sessionId]);

  // Load history when switching to an existing session
  useEffect(() => {
    let cancelled = false;
    async function loadHistory(id: number) {
      try {
        const data: any = await getSession(id);
        if (cancelled) return;
        // Try to extract a conversation id and persist it
        const conv =
          data?.conversation?.id ||
          data?.conversationId ||
          data?.conversationID ||
          data?.conversation_id ||
          data?.conv_id ||
          null;
        if (conv) {
          setConversationId(Number(conv));
          try {
            localStorage.setItem("active_conversation_id", String(conv));
          } catch {}
          // If a knowledge/agent context is active but wasn't in createSession payload, patch the conversation now
          try {
            const ak = (typeof window !== 'undefined') ? localStorage.getItem('active_knowledge_id') : null;
            const aa = (typeof window !== 'undefined') ? localStorage.getItem('active_agent_id') : null;
            if (ak) {
              const api = await import("@/lib/api");
              await api.updateConversation(Number(conv), { explore_type: 'knowledge', explore_name: ak, knowledge_id: ak });
            } else if (aa) {
              const api = await import("@/lib/api");
              await api.updateConversation(Number(conv), { explore_type: 'agent', explore_name: aa, agent_id: Number.isFinite(Number(aa)) ? Number(aa) : aa });
            }
          } catch {}
        }
        // Try common shapes: {messages:[{role/content}]}, {history:[...]} or {items:[...]}
        const raw = data?.messages || data?.history || data?.items || [];
        const mapMsgs = (arr: any[]) =>
          (arr || []).map((m: any) => {
            const text = m?.text ?? m?.content ?? m?.message ?? "";
            const roleRaw = m?.role ?? m?.sender ?? m?.side;
            const sender = roleRaw
              ? /user|human/i.test(String(roleRaw))
                ? "user"
                : "bot"
              : m?.is_user === true || m?.is_bot === false
              ? "user"
              : "bot";
            return { sender, text } as { sender: string; text: string };
          });
        let mapped = Array.isArray(raw) ? mapMsgs(raw) : [];
        if (!mapped.length) {
          try {
            const res: any = await listMessages(id);
            const arr = Array.isArray(res)
              ? res
              : res?.items || res?.messages || [];
            mapped = mapMsgs(arr);
          } catch {}
        }
        if (!mapped.length && (conversationId ?? conv)) {
          try {
            const convId = conversationId ?? conv;
            const res: any = await listMessagesByConversation(convId);
            const arr = Array.isArray(res)
              ? res
              : res?.items || res?.messages || res?.data || [];
            mapped = mapMsgs(arr);
          } catch {}
        }
        if (!mapped.length) {
          try {
            const res: any = await listMessagesByConversation(id);
            const arr = Array.isArray(res)
              ? res
              : res?.items || res?.messages || res?.data || [];
            mapped = mapMsgs(arr);
          } catch {}
        }
        if (mapped.length) setMessages(mapped);
      } catch {
        // ignore if cannot load
      }
    }
    if (sessionId) loadHistory(sessionId);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Auto-scroll to bottom on new messages or while loading
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim() && !file) return;

    const userMessage = {
      sender: "user",
      text: input || "",
      fileName: file?.name || undefined,
    };
    // Fire-and-forget analytics log
    try {
      fetch('/api/analytics/log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: (input || '').trim() }) });
    } catch {}
    // Pre-check credit before sending (estimate prompt tokens only)
    try {
      const check = await precheckCanSend({
        promptText: userMessage.text,
        model: modelChoice?.model || undefined,
      });
      // If credit is insufficient, block sending and show a clear alert
      if (!check.ok) {
        const need = (check.cost || 0).toLocaleString();
        const bal = (check.balance || 0).toLocaleString();
        setSnackbarMessage(`เครดิตไม่พอ ต้องใช้ ${need} แต่คงเหลือ ${bal} กรุณาเติมเครดิตหรือลดความยาวข้อความ`);
        setSnackbarOpen(true);
        return;
      }
      if (!check.ok) {
        setSnackbarMessage(`เครดิตไม่พอ ต้องใช้ ${check.cost} คงเหลือ ${check.balance}`);
        setSnackbarOpen(true);
        return;
      }
    } catch {}

    lastUsageRef.current = null; // reset previous usage snapshot for a new turn
    setMessages((prev) => {
      userIndexRef.current = prev.length;
      botIndexRef.current = null;
      return [...prev, userMessage];
    });
    setInput("");
    setLoading(true);

    try {
      const attached = file; // keep reference before clearing
      setRefreshKey((k) => k + 1);

      let sid = sessionId;
      if (!sid) {
        // Ensure we have a user ID before creating session
        try {
          const api = await import("@/lib/api");
          await api.ensureUserId();
        } catch {}
        
        const uid = (typeof window !== 'undefined') ? localStorage.getItem('user_id') : undefined;
        // If user has selected an active agent/knowledge earlier, propagate into session creation
        let opts: any = { ...modelChoice };
        try {
          const ak = (typeof window !== 'undefined') ? localStorage.getItem('active_knowledge_id') : null;
          const aa = (typeof window !== 'undefined') ? localStorage.getItem('active_agent_id') : null;
          if (ak) {
            opts.explore_type = 'knowledge';
            opts.explore_name = ak;
            opts.knowledge_id = ak;
          } else if (aa) {
            opts.explore_type = 'agent';
            opts.explore_name = aa;
            opts.agent_id = Number.isFinite(Number(aa)) ? Number(aa) : aa;
          }
        } catch {}
        const s = await createSession(undefined, opts);
        sid = s.id;
        setSessionId(sid);
        if (typeof window !== "undefined") {
          localStorage.setItem("active_session_id", String(sid));
        }
        try {
          router.push(`/c/${sid}`);
        } catch {}
        // Set session title to first user message
        const title = (userMessage.text || "").trim();
        if (title) {
          try {
            await updateSession(sid, { title: title.slice(0, 80) });
          } catch {}
        }
        // Ensure the session is associated with a user and conversation too
        try {
          if (uid) {
            await updateSession(sid, { user_id: uid, userId: uid, owner_id: uid, ownerId: uid, created_by: uid, createdBy: uid });
            try {
              const detail: any = await getSession(sid);
              const conv = detail?.conversation?.id || detail?.conversation_id || detail?.conversationId || detail?.conv_id || null;
              if (conv != null) {
                const api = await import("@/lib/api");
                await api.updateConversation(conv, { user_id: uid, userId: uid, owner_id: uid, ownerId: uid, created_by: uid, createdBy: uid });
                try { localStorage.setItem('active_conversation_id', String(conv)); } catch {}
              }
            } catch {}
          }
        } catch {}
        // Read conversation id from create response if exists
        const conv =
          (s as any)?.conversation?.id ||
          (s as any)?.conversationId ||
          (s as any)?.conversationID ||
          (s as any)?.conversation_id;
        if (conv) {
          setConversationId(Number(conv));
          try {
            localStorage.setItem("active_conversation_id", String(conv));
          } catch {}
        }
      }

      let response: any;
      if (attached) {
        try {
          response = await sendChatMessageWithFile(
            sid!,
            userMessage.text,
            (modelChoice?.provider === 'auto' ? pickAutoModel(userMessage.text, attached) : modelChoice),
            attached,
            conversationId ?? sid!
          );
        } catch (err) {
          console.warn(
            "Upload to backend failed; falling back to local upload",
            err
          );
          // Fallback: upload to local Next route then send URL as message
          try {
            const fd2 = new FormData();
            fd2.append("file", attached, attached.name);
            const up = await fetch("/api/files/upload", {
              method: "POST",
              body: fd2,
            });
            if (!up.ok) throw new Error(`local upload ${up.status}`);
            const data = await up.json();
            const url = data?.url || data?.href;
            const combined = [userMessage.text, url].filter(Boolean).join("\n");
            response = await sendChatMessage(
              sid!,
              combined,
              (modelChoice?.provider === 'auto' ? pickAutoModel(combined, null) : modelChoice),
              conversationId ?? sid!
            );
          } catch (err2) {
            console.error("Local upload fallback failed", err2);
            throw err2;
          }
        }
      } else {
        const finalChoice = (modelChoice?.provider === 'auto' ? pickAutoModel(userMessage.text, null) : modelChoice);
        response = await sendChatMessage(
          sid!,
          userMessage.text,
          finalChoice,
          conversationId ?? sid!
        );
      }
      const convFromResponse =
        response?.conversation?.id ||
        response?.conversationId ||
        response?.conversationID ||
        response?.conversation_id ||
        response?.conv_id ||
        null;

      if (convFromResponse) {
        const nextConv = Number(convFromResponse);
        if (!Number.isNaN(nextConv)) {
          setConversationId(nextConv);
          try {
            localStorage.setItem("active_conversation_id", String(nextConv));
          } catch {}
          // Best-effort attach user_id to conversation record as well
          try {
            const uid = (typeof window !== 'undefined') ? (localStorage.getItem('user_id') || undefined) : undefined;
            if (uid) {
              const api = await import("@/lib/api");
              await api.updateConversation(nextConv, { user_id: uid, userId: uid });
            }
          } catch {}
        }
      }

      const usage =
        (response.usage || response.token_usage || response.meta?.usage) ??
        null;
      const p = usage?.prompt_tokens ?? usage?.input_tokens ?? 0;
      const c = usage?.completion_tokens ?? usage?.output_tokens ?? 0;
      const t = usage?.total_tokens ?? p + c;

      const aiMessage = {
        sender: "bot",
        text: response.reply || "",
        completionTokens: c,
        totalTokens: t,
      };
      setMessages((prev) => {
        const next = [...prev];
        if (userIndexRef.current != null && next[userIndexRef.current]) {
          next[userIndexRef.current] = {
            ...next[userIndexRef.current]!,
            promptTokens: p,
          };
        }
        return [...next, aiMessage];
      });

      // Apply credit charge
      try {
        if (Number(p) > 0 || Number(c) > 0) {
          await applyUsageCost(
            {
              prompt: Number(p || 0),
              completion: Number(c || 0),
              total: Number(t || p + c || 0),
            },
            {
              model: modelChoice?.model || null,
              conversationId: conversationId ?? sid!,
            }
          );
        } else {
          const estIn = await estimatePromptTokens(userMessage.text || "");
          const estOut = await estimatePromptTokens(aiMessage.text || "");
          await applyUsageCost(
            { prompt: estIn, completion: estOut, total: estIn + estOut },
            {
              model: modelChoice?.model || null,
              conversationId: conversationId ?? sid!,
            }
          );
        }
      } catch {}
    } catch (err: any) {
      const raw = err?.message ? String(err.message) : TH.apiError;
      const looksHtml = /<\s*!doctype|<\s*html[\s>]/i.test(raw || "");
      const msg = looksHtml ? `${TH.apiError}` : raw;
      setMessages((prev) => [...prev, { sender: "bot", text: msg }]);
    } finally {
      setFile(null);
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!loading) sendMessage();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (
      selectedFile &&
      (selectedFile.type === "application/pdf" ||
        selectedFile.type.startsWith("image/"))
    ) {
      setFile(selectedFile);
    } else {
      setSnackbarMessage("รองรับเฉพาะ PDF หรือไฟล์รูปภาพเท่านั้น");
      setSnackbarOpen(true);
      e.target.value = "";
    }
  };

  // Log message for analytics (MCQ + moderation)
  async function logForAnalytics(text: string) {
    try {
      const uid = getUserId?.() || undefined;
      await fetch('/api/analytics/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, userId: uid }),
      });
    } catch {}
  }

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <div className="flex flex-col h-full bg-gray-900/90 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="chat-aurora-ring animate-chat-orbit" />
        <div className="chat-aurora-glow animate-chat-glow" />
        <div className="chat-aurora-light animate-chat-light" />
        <div className="chat-grid-mask" />
      </div>
      <div className="flex-1 overflow-y-auto p-3 pb-28" ref={scrollRef}>
        <div className="chat-width mx-auto px-4 space-y-3">
          {(agentActive || knowledgeCount > 0) && (
            <div className="flex items-center gap-2 sticky top-0 z-10">
              {agentActive && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-600 text-white">Agent AI</span>
              )}
              {knowledgeCount > 0 && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-600 text-white">Knowledge</span>
              )}
            </div>
          )}
          {!hydrated && (
            <div className="text-center text-gray-400 text-sm">Loading.</div>
          )}
          {/* Empty-state start screen */}
          {hydrated && messages.length === 0 && !loading && (
            <div className="mx-auto max-w-2xl text-center py-8 text-gray-200">
              <div className="text-2xl font-semibold mb-2">เริ่มต้นสนทนา</div>
              <div className="text-sm text-gray-400 mb-4">
                พิมพ์คำถามของคุณ แนบไฟล์ หรือเลือกตัวอย่างด้านล่างเพื่อเริ่มต้น
              </div>
              {(agentActive || knowledgeCount > 0) && (
                <div className="flex items-center justify-center gap-2 mb-4">
                  {agentActive && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-600 text-white">Agent AI</span>
                  )}
                  {knowledgeCount > 0 && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-600 text-white">Knowledge</span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <button className="rounded-lg border border-gray-700 bg-gray-800/60 hover:bg-gray-800 text-left px-3 py-2 text-sm"
                  onClick={() => quickSend('อธิบาย “มาตรา 14” ของ พ.ร.บ. คอมพิวเตอร์ แบบเข้าใจง่าย พร้อมยกตัวอย่าง')}>อธิบายหัวข้อกฎหมายแบบเข้าใจง่าย</button>
                <button className="rounded-lg border border-gray-700 bg-gray-800/60 hover:bg-gray-800 text-left px-3 py-2 text-sm"
                  onClick={() => quickSend('สรุปใจความสำคัญจากไฟล์ที่แนบ เป็น bullet 5 ข้อ พร้อมอ้างอิงหน้า')}>สรุปจากไฟล์/เอกสาร</button>
                <button className="rounded-lg border border-gray-700 bg-gray-800/60 hover:bg-gray-800 text-left px-3 py-2 text-sm"
                  onClick={() => quickSend('ช่วยร่างอีเมลเชิงทางการ ขอข้อมูลเพิ่มเติมเกี่ยวกับหัวข้อที่คุยกัน')}>ช่วยร่างอีเมล</button>
                <button className="rounded-lg border border-gray-700 bg-gray-800/60 hover:bg-gray-800 text-left px-3 py-2 text-sm"
                  onClick={() => quickSend('สกัดรายการคำสำคัญ (keywords) ที่เกี่ยวข้อง พร้อมคำอธิบายสั้นๆ')}>สกัดคำสำคัญ</button>
              </div>

              {/* FAQ */}
              <div className="text-sm text-gray-400 mt-6 mb-2">คำถามที่พบบ่อย</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {(dbFaqs && dbFaqs.length > 0 ? dbFaqs : getDefaultFaqs()).map((f, i) => (
                  <button key={i}
                    className="rounded-full px-3 py-1.5 text-xs bg-gray-800/70 hover:bg-gray-800 border border-gray-700"
                    onClick={() => quickSend(f.query)}
                    title={f.query}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, idx) =>
            msg.sender === "user" ? (
              <div key={idx} className="flex justify-end">
                <div className="flex flex-col items-end max-w-[80%]">
                  {msg.fileName && (
                    <div className="text-xs italic text-gray-300 mb-1 text-right">
                      {TH.fileLabel} {msg.fileName}
                    </div>
                  )}
                  <div className="inline-block rounded-2xl bg-indigo-600 text-white px-4 py-2 shadow text-left animate-chat-bubble">
                    <MessageBody text={msg.text} />
                  </div>
                  {typeof msg.promptTokens === "number" &&
                    msg.promptTokens > 0 && (
                      <div className="text-[11px] text-gray-400 mt-1 text-right">
                        in {msg.promptTokens} tokens
                      </div>
                    )}
                </div>
              </div>
            ) : (
              <div
                key={idx}
                className="mr-auto w-full text-gray-200 leading-relaxed animate-chat-reply"
              >
                <div className="max-w-[80%]">
                  <MessageBody text={msg.text} />
                </div>
                {typeof msg.completionTokens === "number" &&
                  msg.completionTokens > 0 && (
                    <div className="text-[11px] text-gray-400 mt-1">
                      out {msg.completionTokens} tokens
                      {typeof msg.totalTokens === "number" &&
                      msg.totalTokens > 0
                        ? ` (total ${msg.totalTokens})`
                        : ""}
                    </div>
                  )}
              </div>
            )
          )}

          {loading && (
            <div className="flex justify-start text-gray-300 italic text-sm animate-chat-typing">
              {TH.loading}
            </div>
          )}
        </div>
        <div ref={bottomRef} />
      </div>

      {/* Drop indicator & preview chip */}
      {dragActive && (
        <div className="pointer-events-none fixed bottom-24 left-[calc(4rem+1rem)] right-4 z-20">
          <div className="rounded-xl border-2 border-dashed border-indigo-400/70 bg-indigo-500/10 h-16 flex items-center justify-center text-indigo-200 text-sm">
            วางไฟล์ที่นี่เพื่อแนบ
          </div>
        </div>
      )}

      {file && (
        <div className="fixed bottom-24 left-[calc(4rem+1rem)] right-4 z-20">
          <div className="flex items-center gap-3 bg-gray-800/80 border border-gray-700 rounded-md px-3 py-2">
            {previewUrl && (
              <img
                src={previewUrl}
                alt={file.name}
                className="h-14 w-14 object-cover rounded border border-gray-700"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-300 truncate">{file.name}</div>
              <div className="text-[11px] text-gray-500">
                {(file.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <button
              onClick={() => setFile(null)}
              className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
            >ลบ</button>
          </div>
        </div>
      )}

      <Paper
        elevation={0}
        className="fixed bottom-7 z-20 px-4 bg-transparent"
        sx={{ backgroundColor: "transparent", boxShadow: "none" }}
        style={{
          left: "calc(4rem + var(--chat-drawer-offset, 0px) + 1rem)",
          right: "1rem",
        }}
      >
        <div className="chat-width w-full mx-auto flex items-center gap-2">
          <Tooltip title="แนบไฟล์ (PDF / รูปภาพ) - ลากวางได้หรือเลือกไฟล์">
            <IconButton
              component="label"
              size="small"
              sx={{ color: "#CBD5E1" }}
            >
              <AttachFileIcon fontSize="small" />
              <input
                type="file"
                hidden
                accept=".pdf,image/*"
                onChange={handleFileChange}
              />
            </IconButton>
          </Tooltip>

          <TextField
            key={refreshKey}
            fullWidth
            variant="outlined"
            placeholder={TH.placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              try {
                const items = (e as any).clipboardData?.items || [];
                for (let i = 0; i < items.length; i++) {
                  const it = items[i];
                  if (
                    it &&
                    it.kind === "file" &&
                    it.type &&
                    String(it.type).startsWith("image/")
                  ) {
                    const blob = it.getAsFile();
                    if (blob) {
                      const ext =
                        (blob.type.split("/")?.[1] || "png")
                          .replace(/[^a-z0-9]+/gi, "")
                          .toLowerCase() || "png";
                      const f = new File([blob], `pasted.${ext}`, {
                        type: blob.type,
                      });
                      setFile(f);
                      e.preventDefault();
                      break;
                    }
                  }
                }
              } catch {}
            }}
            multiline
            minRows={1}
            maxRows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(17, 24, 39, 0.65)",
                backdropFilter: "blur(8px)",
                borderRadius: "24px",
                color: "#E5E7EB",
                minHeight: "50px",
                boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
                "& fieldset": { borderColor: "rgba(75, 85, 99, 0.6)" },
                "&:hover fieldset": { borderColor: "rgba(148, 163, 184, 0.8)" },
                "&.Mui-focused fieldset": { borderColor: "#6366F1" },
              },
              "& .MuiInputBase-input": {
                color: "#E5E7EB",
                paddingTop: "10px",
                paddingBottom: "10px",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={sendMessage}
                    disabled={loading}
                    sx={{
                      bgcolor: "#4F46E5",
                      color: "white",
                      "&:hover": { bgcolor: "#6366F1" },
                      boxShadow: "0 6px 14px rgba(79,70,229,0.35)",
                      ml: 0.5,
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
      </Paper>

      <div className="fixed bottom-0 left-[calc(4rem+1rem)] right-4 py-2 text-xs text-gray-400 text-center">
        @fellows V1
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}










